import {Path, Range, Text, type Editor, type Node} from "slate";

import {isKhanmigoEditing, khanmigoEditStartKey} from "@khan/guide-math-editor";

import {isEditorFocused} from "./editor-helpers.ts";

/**
 * Gets the cursor decoration for the current node and path, if we want to
 * show one. We use this to keep the selected text visible in the editor even
 * when the editor has lost focus. Thus, we highlight text nodes that intersect
 * with the current selection ONLY when the editor is not focused.
 *
 * This is also important for highlighting Khanmigo's edits as they come in.
 * While Khanmigo is editing, the editor is read-only (to humans) but we want
 * to make it clear where text is being added.
 *
 * We use temporary decorations instead of highlight block nodes to avoid
 * altering the Slate data model and polluting the undo/redo history with
 * highlight manipulations.
 *
 * For more on decorations in Slate:
 * https://docs.slatejs.org/concepts/09-rendering#decorations
 */
export const getCursorDecorationForNode = (
    editor: Editor | null,
    node: Node,
    path: Path,
): Range | null => {
    if (!editor || !editor.selection || !Text.isText(node)) {
        // We won't show any highlight decorations if there's no selection or
        // if the current node isn't a text node (we only apply the highlight
        // mark to leaf nodes).
        return null;
    }

    if (isKhanmigoEditing(editor)) {
        // We also don't show any cursor highlights while Khanmigo is editing.
        return null;
    }

    // First, we need to check if Khanmigo is editing or not. If so, we use the
    // edit start point as the beginning of the cursor decoration.
    let cursorStart = editor[khanmigoEditStartKey];
    if (!cursorStart) {
        // Khanmigo is not actively editing; we'll only show a cursor if the
        // editor isn't focused.
        if (isEditorFocused()) {
            return null;
        }

        // If Khanmigo isn't actually editing, but the editor isn't focused,
        // we use the user's selection start point as the beginning of the
        // decoration. This keeps the selection visible on blur.
        cursorStart = Range.start(editor.selection);
    }

    // The decoration end is always the end of the selection, AKA the insertion
    // point for new edits.
    const cursorEnd = Range.end(editor.selection);

    if (
        Path.isBefore(path, cursorStart.path) ||
        Path.isAfter(path, cursorEnd.path)
    ) {
        // The path for the current node has no overlap at all with the edit
        // range, so bail out - nothing to highlight.
        return null;
    }

    // By default, we have the range span the full node:
    const anchor = {path, offset: 0};
    const focus = {path, offset: node.text.length};
    let atEnd = false;

    if (Path.equals(path, cursorStart.path)) {
        // But if this is the path where the selection starts, we trim the
        // beginning to start at the same offset as the selection.
        anchor.offset = cursorStart.offset;
    }

    if (Path.equals(path, cursorEnd.path)) {
        // And if this is the path where the selection end, we trim the end
        // to end at the same offset as the selection.
        focus.offset = cursorEnd.offset;
        // Setting this is important for the GuideCursor component to mark
        // the very end of the highlighted section with a cursor line:
        atEnd = true;
    }

    return {anchor, focus, guideCursor: {atEnd}};
};

/**
 * Determines if a path should be highlighted (decorated) to indicate that
 * it is selected by Khanmigo. This is similar to the helper function above,
 * but simpler: here, we don't need to determine the exact anchor/focus points
 * of the selection, but just to check if Khanmigo's selection intersects at
 * all with the given path.
 *
 * Note that the range of Khanmigo's selection either spans the actual range of
 * editor.selection or, if Khanmigo is actively typing, it starts at the point
 * where Khanmigo began editing and ends at the end of editor.selection. This
 * allows us to keep all of Khanmigo's typed text highlighted as it writes.
 */
export const shouldHighlightNodePath = (
    editor: Editor | null,
    path: Path,
): boolean => {
    if (!editor || !editor.selection) {
        // We won't show any highlight decorations if there's no selection.
        return false;
    }

    if (isKhanmigoEditing(editor)) {
        // We also don't show any cursor highlights while Khanmigo is editing.
        return false;
    }

    // First, we need to check if Khanmigo is editing or not. If so, we use the
    // edit start point as the beginning of the cursor decoration.
    let cursorStart = editor[khanmigoEditStartKey];
    if (!cursorStart) {
        // Khanmigo is not actively editing; we'll only show a highlight if the
        // editor isn't focused.
        if (isEditorFocused()) {
            return false;
        }

        // If Khanmigo isn't actually editing, but the editor isn't focused,
        // we use the user's selection start point as the beginning of the
        // decoration. This keeps the selection visible on blur.
        cursorStart = Range.start(editor.selection);
    }

    // The decoration end is always the end of the selection, AKA the insertion
    // point for new edits.
    const cursorEnd = Range.end(editor.selection);

    if (
        Path.isBefore(path, cursorStart.path) ||
        Path.isAfter(path, cursorEnd.path)
    ) {
        // The path for the current node has no overlap at all with the edit
        // range, so bail out - nothing to highlight.
        return false;
    }

    return true;
};
